import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { stripHtml } from '../../lib/format';
import { PageHead, Section, Spinner, ErrorNote, Empty, useAsync } from '../../components/ui';
import RichText from '../../components/RichText';
import Modal from '../../components/Modal';
import RichTextEditor from '../../components/RichTextEditor';
import CourseInvitePanel from '../../components/CourseInvitePanel';
import PracticeInvitePanel from '../../components/PracticeInvitePanel';
import GroupInvitePanel from '../../components/GroupInvitePanel';
import EventInvitePanel from '../../components/EventInvitePanel';
import { useRole } from '../../app/RoleContext';
import { useAuth } from '../../auth/AuthContext';
import { RemoveMemberButton, money, useMemberPayments } from '../../components/RemoveMember';
import '../../styles/learn.css';

// Members shows only what the signed-in person created (like the Manage
// pages): courses/practices they authored, groups they lead, events they own.
// 2026-10-17 — superadmins see every program in Manage members (pass isAdmin).
const mineOnly = (list, test, isAdmin = false) =>
  (Array.isArray(list) ? (isAdmin ? list : list.filter(test)) : []);

// One list layout for all four tabs (matches Groups): name + one line of
// detail | member count | ›. Counts come from /member-counts/{kind}.
function ItemTable({ heading, items, counts, onOpen }) {
  return (
    <table className="tbl">
      <thead><tr><th>{heading}</th><th>Price</th><th>Members</th><th></th></tr></thead>
      <tbody>
        {items.map((it) => (
          <tr key={it.id} className="clickable" style={{ cursor: 'pointer' }} onClick={() => onOpen(it.id)}>
            <td><b>{it.name}</b>{it.sub && <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>{it.sub}</div>}</td>
            <td>{it.priceCents > 0
              ? <span className="pill" style={{ background: '#dcfce7', color: '#166534', fontWeight: 700 }}>{money(it.priceCents)}</span>
              : <span style={{ color: 'var(--muted)' }}>Free</span>}</td>
            <td>{counts?.[it.id] ?? it.count ?? 0}</td>
            <td style={{ textAlign: 'right', color: 'var(--muted-2)' }}>›</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// design/030 — who paid, at a glance.
function PaymentCell({ m, payment }) {
  const pill = (bg, fg, text, title) => (
    <span className="pill" title={title} style={{ background: bg, color: fg, fontWeight: 700 }}>{text}</span>
  );
  if (m.owner) return <span style={{ color: 'var(--muted)' }}>—</span>;
  if (payment) {
    if (payment.refundedCents > 0 && payment.refundableCents === 0) {
      return pill('#f3f4f6', 'var(--muted)', `Refunded ${money(payment.amountCents, payment.currency)}`);
    }
    return (
      <span>
        {pill('#dcfce7', '#166534', `💳 Paid ${money(payment.amountCents, payment.currency)}`)}
        {payment.refundedCents > 0 && (
          <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 2 }}>
            {money(payment.refundedCents, payment.currency)} refunded
          </div>
        )}
      </span>
    );
  }
  if (m.source === 'STRIPE') {
    return pill('#dcfce7', '#166534', '💳 Paid', 'Paid before amounts were recorded in the app — see the Stripe dashboard');
  }
  if (m.source === 'INVITE') return pill('#eef2ff', '#3730a3', 'Invited · free', 'Joined free through your invite — nothing to refund');
  if (m.source === 'SELF_FREE' || m.source === 'BACKFILL') {
    return pill('#f3f4f6', 'var(--muted)', 'No charge', 'Joined while this was free (the price was added later) — nothing to refund');
  }
  return <span style={{ color: 'var(--muted)' }} title="Joined before payments were recorded in the app">Not recorded</span>;
}

function useCounts(kind) {
  return useAsync(() => api.memberCounts(kind).catch(() => ({})), [kind]);
}

const htmlEmpty = (h) => !h || !h.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

// Members management (design/013 + design/021 tabbed rework). One page,
// up to four tabs — Courses, Practices, Groups, Events. Publishers see all
// four; Event Leaders (without Publisher) see only Events.
// design/032: tabs follow capabilities — Group Leaders see Groups, Event
// Leaders see Events, Publishers / superadmin see all five incl. Leaders.
const ALL_TABS = [
  { key: 'courses',   label: 'Courses',   requires: 'publish' },
  { key: 'practices', label: 'Practices', requires: 'publish' },
  { key: 'groups',    label: 'Groups',    requires: 'groups' },
  { key: 'events',    label: 'Events',    requires: 'events' },
  { key: 'leaders',   label: 'Leaders',   requires: 'publish' },
];

export default function MembersAdminPage() {
  const { canPublish, canManageGroups, canCreateEvents } = useRole();
  const tabs = ALL_TABS.filter((t) =>
    (t.requires === 'publish' && canPublish)
    || (t.requires === 'groups' && canManageGroups)
    || (t.requires === 'events' && canCreateEvents));
  // ?tab=groups&id=… opens one program / offering directly (delete prompts link here).
  const [params] = useSearchParams();
  const initialId = params.get('id');
  const [tab, setTab] = useState(params.get('tab') || tabs[0]?.key);
  // If capabilities change and current tab is no longer available, snap to first.
  const currentValid = tabs.some((t) => t.key === tab);
  const active = currentValid ? tab : tabs[0]?.key;

  return (
    <div className="page">
      <PageHead icon="🧑‍🎓" title="Manage your members"
        sub={canPublish
          ? 'Open a program to see its members and send invites, or manage Event and Group Leaders'
          : 'Open a program to see its members and send invites'} />
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, borderBottom: '1px solid var(--border)' }}>
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`btn btn-sm ${active === t.key ? 'btn-primary' : ''}`}
            style={{ borderRadius: '8px 8px 0 0', marginBottom: -1 }}
          >{t.label}</button>
        ))}
      </div>
      {active === 'courses'   && <CoursesTab initialId={tab === 'courses' ? initialId : null} />}
      {active === 'practices' && <PracticesTab initialId={tab === 'practices' ? initialId : null} />}
      {active === 'groups'    && <GroupsTab initialId={tab === 'groups' ? initialId : null} />}
      {active === 'events'    && <EventsTab initialId={tab === 'events' ? initialId : null} />}
      {active === 'leaders'   && <LeadersTab />}
    </div>
  );
}

// design/030 — one member table for all four tabs: Member (Owner label,
// Paid pill, email) | Joined | [extra] | Remove.
function MemberRows({ kind, itemId, itemName, emptyText, extraHead, renderExtra, rowsFn, deps, onRowClick, renderDetail }) {
  const { loading, error, data, reload } = useAsync(
    rowsFn || (() => api.memberList(kind, itemId).then((r) => (Array.isArray(r) ? r : []))), deps || [kind, itemId]);
  const pay = useMemberPayments(kind, itemId);
  const refresh = () => { reload(); pay.reload(); };
  const [openId, setOpenId] = useState(null);
  if (loading) return <Spinner />;
  if (error) return <ErrorNote error={error} onRetry={reload} />;
  // Payment column whenever the item costs money or anyone has a recorded payment.
  const showPayment = !!pay.data?.priced || Object.keys(pay.data?.payments || {}).length > 0;
  const cols = 3 + (extraHead ? 1 : 0) + (showPayment ? 1 : 0);
  return (
    <Section style={{ padding: 0 }}>
      <div className="list-toolbar"><div className="section-title">{itemName}</div></div>
      {data.length === 0 ? <div style={{ padding: 18 }}><Empty>{emptyText}</Empty></div> : (
        <table className="tbl">
          <thead><tr><th>Member</th>{showPayment && <th>Payment</th>}<th>Joined</th>{extraHead && <th>{extraHead}</th>}<th></th></tr></thead>
          <tbody>
            {data.map((m) => {
              const name = [m.firstname, m.lastname].filter(Boolean).join(' ') || m.username || m.name;
              const payment = pay.data?.payments?.[m.id];
              const open = renderDetail && openId === m.id;
              return [
                <tr key={m.id} className={renderDetail ? 'clickable' : undefined}
                  style={renderDetail ? { cursor: 'pointer' } : undefined}
                  onClick={renderDetail ? () => setOpenId(open ? null : m.id) : undefined}>
                  <td>
                    <b>{name}</b>
                    {m.owner && <span className="pill" style={{ marginLeft: 6, background: '#fef3c7', color: '#92400e' }}>Owner</span>}
                    <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>{m.username && m.username !== m.email ? `@${m.username}` : (m.email || '')}</div>
                  </td>
                  {showPayment && <td><PaymentCell m={m} payment={payment} /></td>}
                  <td style={{ fontSize: '0.8125rem', color: 'var(--muted)' }}>{m.joinedAt ? fmtDate(m.joinedAt) : '—'}</td>
                  {extraHead && <td>{renderExtra(m)}</td>}
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                    {!m.owner && (
                      <RemoveMemberButton kind={kind} itemId={itemId} itemName={itemName} personId={m.id}
                        name={name} payment={payment} priced={pay.data?.priced} onRemoved={refresh} />
                    )}
                    {renderDetail && <span style={{ marginLeft: 8, color: 'var(--muted-2)' }}>{open ? '▾' : '▸'}</span>}
                  </td>
                </tr>,
                open && (
                  <tr key={`${m.id}-d`}>
                    <td colSpan={cols} style={{ background: 'var(--card-soft)' }}>{renderDetail(m)}</td>
                  </tr>
                ),
              ];
            })}
          </tbody>
        </table>
      )}
    </Section>
  );
}

// ---- Courses tab ----------------------------------------------------------

function CoursesTab({ initialId }) {
  const { me } = useAuth();
  const { loading, error, data: courses, reload } = useAsync(
    // Mine + old courses with no recorded owner (shared, see V54).
    () => api.adminListCourses().then((c) => mineOnly(c, (x) => !x.authorPersonId || (me?.id && x.authorPersonId === me.id), me?.isAdmin)), [me?.id, me?.isAdmin]);
  const [courseId, setCourseId] = useState(initialId || null);
  const counts = useCounts('course');
  if (loading) return <Spinner />;
  if (error) return <ErrorNote error={error} onRetry={reload} />;
  const course = courses.find((c) => c.id === courseId) || null;
  if (course) {
    return (
      <>
        <a onClick={() => { setCourseId(null); counts.reload(); }} style={{ cursor: 'pointer', fontSize: '0.8125rem' }}>← All courses</a>
        <CourseMembers course={course} />
        <CourseInvitePanel courseId={course.id} priced={(course.priceCents || 0) > 0} />
      </>
    );
  }
  return (
    <Section style={{ padding: 0 }}>
      {courses.length === 0 ? <div style={{ padding: 18 }}><Empty>You haven't created any courses yet.</Empty></div> : (
        <ItemTable heading="Course" counts={counts.data} onOpen={setCourseId}
          items={courses.map((c) => ({
            id: c.id, name: c.title, priceCents: c.priceCents,
            sub: [stripHtml(c.summary), c.status !== 'PUBLISHED' ? 'Draft' : null,
              !c.authorPersonId ? 'Shared — no owner recorded' : null].filter(Boolean).join(' · '),
          }))} />
      )}
    </Section>
  );
}

// ---- Practices tab --------------------------------------------------------

function PracticesTab({ initialId }) {
  const { me } = useAuth();
  const { loading, error, data, reload } = useAsync(
    () => api.adminListPractices().then((p) => mineOnly(p, (x) => me?.id && x.authorPersonId === me.id, me?.isAdmin)), [me?.id, me?.isAdmin]);
  const [practiceId, setPracticeId] = useState(initialId || null);
  const counts = useCounts('practice');
  if (loading) return <Spinner />;
  if (error) return <ErrorNote error={error} onRetry={reload} />;
  const practice = data.find((p) => p.id === practiceId) || null;
  if (practice) {
    return (
      <>
        <a onClick={() => { setPracticeId(null); counts.reload(); }} style={{ cursor: 'pointer', fontSize: '0.8125rem' }}>← All practices</a>
        <PracticeMembers practice={practice} />
        <PracticeInvitePanel practiceId={practice.id} priced={(practice.priceCents || 0) > 0} />
      </>
    );
  }
  return (
    <Section style={{ padding: 0 }}>
      {data.length === 0 ? <div style={{ padding: 18 }}><Empty>You haven't created any practices yet.</Empty></div> : (
        <ItemTable heading="Practice" counts={counts.data} onOpen={setPracticeId}
          items={data.map((p) => ({
            id: p.id, name: p.title, priceCents: p.priceCents,
            sub: [p.subtitle, p.status !== 'PUBLISHED' ? 'Draft' : null].filter(Boolean).join(' · '),
          }))} />
      )}
    </Section>
  );
}

function PracticeMembers({ practice }) {
  return <MemberRows kind="practice" itemId={practice.id} itemName={practice.title}
    emptyText="No one is enrolled in this practice yet."
    renderDetail={(m) => <PracticeMemberDetail practiceId={practice.id} member={m} />} />;
}

// 2026-10-15 — same "N reflections · N questions" link as courses. Practices
// have no lessons, so there's no progress card.
function PracticeMemberDetail({ practiceId, member }) {
  const navigate = useNavigate();
  const who = member.username || member.email || '';
  const { loading, error, data, reload } = useAsync(
    () => api.responsesList({ status: 'all', program: `practice:${practiceId}`, q: who })
      .then((r) => (r?.items || []).filter((x) => x.learnerId === member.id)),
    [practiceId, member.id]);
  if (loading) return <div style={{ padding: 12 }}><Spinner /></div>;
  if (error) return <div style={{ padding: 12 }}><ErrorNote error={error} onRetry={reload} /></div>;
  const reflections = data.filter((x) => x.kind === 'REFLECTION').length;
  const questions = data.filter((x) => x.kind === 'QUESTION').length;
  return (
    <div style={{ padding: 16 }}>
      <DetailCard title="Reflections & questions">
        {reflections + questions === 0
          ? <div style={{ color: 'var(--muted)' }}>No reflections or questions yet.</div>
          : (
            <a onClick={() => navigate(`/learner-responses?status=all&program=practice:${practiceId}&q=${encodeURIComponent(who)}`)}
              style={{ cursor: 'pointer' }}>
              {reflections} reflection{reflections === 1 ? '' : 's'} · {questions} question{questions === 1 ? '' : 's'} — open in Learner Responses ↗
            </a>
          )}
      </DetailCard>
    </div>
  );
}

// ---- Groups tab -----------------------------------------------------------

function GroupsTab({ initialId }) {
  const { me } = useAuth();
  const { loading, error, data, reload } = useAsync(
    () => api.listGroups().then((g) => mineOnly(g, (x) => me?.id && x.admin?.id === me.id, me?.isAdmin)), [me?.id, me?.isAdmin]);
  const [groupId, setGroupId] = useState(initialId || null);
  // Same owner-excluded counts as the other tabs (the group list's own
  // member count includes the leader).
  const counts = useCounts('group');
  if (loading) return <Spinner />;
  if (error) return <ErrorNote error={error} onRetry={reload} />;
  const group = data.find((g) => g.id === groupId) || null;
  if (group) {
    return (
      <>
        <a onClick={() => { setGroupId(null); reload(); counts.reload(); }} style={{ cursor: 'pointer', fontSize: '0.8125rem' }}>← All groups</a>
        <GroupMembers groupId={group.id} groupName={group.name} />
        <GroupInvitePanel groupId={group.id} priced={(group.priceCents || 0) > 0} />
      </>
    );
  }
  return (
    <Section style={{ padding: 0 }}>
      {data.length === 0 ? <div style={{ padding: 18 }}><Empty>You don't lead any groups yet.</Empty></div> : (
        <ItemTable heading="Group" counts={counts.data} onOpen={setGroupId}
          items={data.map((g) => ({ id: g.id, name: g.name, sub: g.description, priceCents: g.priceCents }))} />
      )}
    </Section>
  );
}

function GroupMembers({ groupId, groupName }) {
  return <MemberRows kind="group" itemId={groupId} itemName={groupName} emptyText="No members yet." />;
}

// ---- Events tab -----------------------------------------------------------

function EventsTab({ initialId }) {
  const { me } = useAuth();
  const { loading, error, data, reload } = useAsync(
    () => api.listEvents().then((e) => mineOnly(e, (x) => me?.username && x.ownerUsername === me.username, me?.isAdmin)), [me?.username, me?.isAdmin]);
  const [eventId, setEventId] = useState(initialId || null);
  const counts = useCounts('event');
  if (loading) return <Spinner />;
  if (error) return <ErrorNote error={error} onRetry={reload} />;
  const event = data.find((e) => e.id === eventId) || null;
  if (event) {
    return (
      <>
        <a onClick={() => { setEventId(null); counts.reload(); }} style={{ cursor: 'pointer', fontSize: '0.8125rem' }}>← All events</a>
        <EventParticipants eventId={event.id} eventName={event.requestName || '(unnamed)'} />
        <EventInvitePanel eventId={event.id} priced={(event.priceCents || 0) > 0} />
      </>
    );
  }
  return (
    <Section style={{ padding: 0 }}>
      {data.length === 0 ? <div style={{ padding: 18 }}><Empty>You haven't created any events yet.</Empty></div> : (
        <ItemTable heading="Event" counts={counts.data} onOpen={setEventId}
          items={data.map((e) => ({
            id: e.id, name: e.requestName || '(unnamed)', priceCents: e.priceCents,
            sub: [e.topic, e.date, e.groupName ? `in ${e.groupName}` : null].filter(Boolean).join(' · '),
          }))} />
      )}
    </Section>
  );
}

function EventParticipants({ eventId, eventName }) {
  return <MemberRows kind="event" itemId={eventId} itemName={eventName} emptyText="No participants yet." />;
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

// ---- Existing course-member drilldown (unchanged from design/013) ---------

function CourseMembers({ course }) {
  // Everyone enrolled, with progress for those who've started; click a row
  // for their lesson-by-lesson detail and submissions.
  const rowsFn = async () => {
    const [enrolled, engaged] = await Promise.all([
      api.memberList('course', course.id).catch(() => []),
      api.adminCourseMembers(course.id).catch(() => []),
    ]);
    const byId = new Map((Array.isArray(engaged) ? engaged : []).map((m) => [m.personId, m]));
    return (Array.isArray(enrolled) ? enrolled : []).map((p) => ({ ...p, progress: byId.get(p.id) || null }));
  };
  return (
    <MemberRows kind="course" itemId={course.id} itemName={course.title}
      emptyText="No one is enrolled in this course yet." rowsFn={rowsFn} deps={[course.id]}
      extraHead="Progress"
      renderExtra={(m) => (m.progress
        ? <span>{m.progress.completedLessons} / {m.progress.totalLessons} lessons
            {m.progress.submissionCount ? <span style={{ color: 'var(--muted)' }}> · {m.progress.submissionCount} submissions</span> : null}</span>
        : <span style={{ color: 'var(--muted)' }}>Not started</span>)}
      renderDetail={(m) => <MemberDetail courseId={course.id} personId={m.id} username={m.username} />} />
  );
}

function MemberDetail({ courseId, personId, username }) {
  const navigate = useNavigate();
  const { loading, error, data, reload } = useAsync(
    () => api.adminCourseMemberDetail(courseId, personId), [courseId, personId]);

  if (loading) return <div style={{ padding: 12 }}><Spinner /></div>;
  if (error) return <div style={{ padding: 12 }}><ErrorNote error={error} onRetry={reload} /></div>;

  const reflections = (data.submissions || []).filter((s) => s.kind === 'REFLECTION');
  const questions = (data.submissions || []).filter((s) => s.kind === 'QUESTION');

  const done = data.progress.filter((p) => p.completed).length;

  return (
    <div style={{ padding: 16, display: 'grid', gap: 14 }}>
      <DetailCard title={`Progress · ${done}/${data.progress.length}`}>
        <div className="member-progress">
          {data.progress.map((p) => (
            <div key={p.lessonId} className={`progress-row${p.completed ? ' done' : ''}`}>
              <span className="progress-dot">{p.completed ? '✓' : ''}</span>{p.title}
            </div>
          ))}
        </div>
      </DetailCard>
      {/* design/033 — reflections + questions live in Learner Responses now. */}
      <DetailCard title="Reflections & questions">
        {reflections.length + questions.length === 0
          ? <div style={{ color: 'var(--muted)' }}>No reflections or questions yet.</div>
          : (
            <a onClick={() => navigate(`/learner-responses?status=all&program=course:${courseId}&q=${encodeURIComponent(username || data.email || data.name || '')}`)}
              style={{ cursor: 'pointer' }}>
              {reflections.length} reflection{reflections.length === 1 ? '' : 's'} · {questions.length} question{questions.length === 1 ? '' : 's'} — open in Learner Responses ↗
            </a>
          )}
      </DetailCard>
    </div>
  );
}

// A white card with an inset toolbar header, for the member-detail sections.
function DetailCard({ title, children }) {
  return (
    <div className="detail-card">
      <div className="list-toolbar"><div className="section-title">{title}</div></div>
      <div className="detail-card-body">{children}</div>
    </div>
  );
}

function SubmissionList({ title, items, empty, onReplied }) {
  return (
    <DetailCard title={title}>
      {items.length === 0 ? <div style={{ color: 'var(--muted)' }}>{empty}</div> : items.map((s) => (
        <div key={s.id} className="detail-row">
          <div className="submission-meta" style={{ marginTop: 0, marginBottom: 6 }}>{s.lessonTitle} · {new Date(s.createdAt).toLocaleString()}</div>
          <RichText html={s.body} />
          <ReplyThread submissionId={s.id} replies={s.replies} onReplied={onReplied} />
        </div>
      ))}
    </DetailCard>
  );
}

// Publisher-side reply thread under a submission. The composer is collapsed
// behind a "Reply" button so an empty editor doesn't dominate the card.
function ReplyThread({ submissionId, replies, onReplied }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const send = async () => {
    if (htmlEmpty(text) || busy) return;
    setBusy(true);
    try { await api.adminReplySubmission(submissionId, text); setText(''); setOpen(false); onReplied?.(); }
    catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };
  const has = (replies || []).length > 0;
  return (
    <>
      {(has || open) && (
        <div className="reply-thread">
          {(replies || []).map((r) => (
            <div key={r.id} className={`reply-bubble${r.byInstructor ? ' from-instructor' : ''}`}>
              <div className="reply-who">{r.byInstructor ? '🎓 Publisher' : 'Learner'} · {new Date(r.createdAt).toLocaleString()}</div>
              <RichText html={r.body} />
            </div>
          ))}
          {open && (
            <div>
              <RichTextEditor value={text} onChange={setText} minHeight={56} placeholder="Reply to this learner…" />
              <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" disabled={busy || htmlEmpty(text)} onClick={send}>{busy ? 'Sending…' : 'Send reply'}</button>
                <button className="btn" onClick={() => { setText(''); setOpen(false); }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
      {!open && (
        <button className="btn btn-primary" style={{ marginTop: 10 }} onClick={() => setOpen(true)}>
          {has ? '↩ Reply' : '↩ Reply to this learner'}
        </button>
      )}
    </>
  );
}

// ---- Leaders tab (design/032) ----------------------------------------------
// Publishers + superadmin: every Event / Group Leader, invite new ones,
// change / remove the leader roles, resend the sign-in email.

const leaderErr = (e) => (e?.data?.message || e?.message || 'Something went wrong').replace(/^[A-Z_]+:\s*/, '');

function RolePills({ l }) {
  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
      {l.eventLeader && <span className="pill" style={{ background: '#dbeafe', color: '#1e40af' }}>Event Leader</span>}
      {l.groupLeader && <span className="pill" style={{ background: 'var(--green-bg, #dcfce7)', color: 'var(--green-text, #166534)' }}>Group Leader</span>}
    </span>
  );
}

function LeadersTab() {
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const { loading, error, data, reload } = useAsync(
    () => api.listLeaders(query || undefined).then((r) => (Array.isArray(r) ? r : [])), [query]);
  const [editing, setEditing] = useState(null);

  const resend = async (l) => {
    if (!window.confirm(`Send ${l.email} a new temporary password?`)) return;
    try { await api.resendLeaderInvite(l.id); alert('Sent.'); }
    catch (e) { alert(leaderErr(e)); }
  };

  return (
    <>
      <form onSubmit={(e) => { e.preventDefault(); setQuery(q.trim()); }}
        style={{ marginBottom: 12, display: 'flex', gap: 8, maxWidth: 480 }}>
        <input className="input" placeholder="Search by name, username, or email" value={q} onChange={(e) => setQ(e.target.value)} />
        <button className="btn">Search</button>
        {query && <button type="button" className="btn" onClick={() => { setQ(''); setQuery(''); }}>Clear</button>}
      </form>
      <Section style={{ padding: 0 }}>
        {loading ? <div style={{ padding: 18 }}><Spinner /></div>
          : error ? <div style={{ padding: 18 }}><ErrorNote error={error} onRetry={reload} /></div>
            : data.length === 0 ? <div style={{ padding: 18 }}><Empty>{query ? 'No leaders match.' : 'No Event or Group Leaders yet — invite one below.'}</Empty></div> : (
              <table className="tbl">
                <thead><tr><th>Leader</th><th>Email</th><th>Role</th><th>Leads</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {data.map((l) => {
                    const name = [l.firstname, l.lastname].filter(Boolean).join(' ') || l.username;
                    const leads = [
                      l.groupsLed ? `${l.groupsLed} group${l.groupsLed === 1 ? '' : 's'}` : null,
                      l.eventsOwned ? `${l.eventsOwned} event${l.eventsOwned === 1 ? '' : 's'}` : null,
                    ].filter(Boolean).join(' · ') || '—';
                    return (
                      <tr key={l.id}>
                        <td>
                          <b>{name}</b>
                          {l.username && l.username !== l.email && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>@{l.username}</div>
                          )}
                        </td>
                        <td>{l.email}</td>
                        <td><RolePills l={l} /></td>
                        <td style={{ color: leads === '—' ? 'var(--muted)' : undefined }}>{leads}</td>
                        <td>{l.invited
                          ? <span className="pill" style={{ background: '#fef3c7', color: '#92400e' }} title="Hasn't signed in and set a password yet">Invited</span>
                          : <span className="pill" style={{ background: '#f3f4f6', color: 'var(--muted)' }}>Active</span>}
                        </td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {l.invited && <button className="btn btn-sm" onClick={() => resend(l)}>Resend invite</button>}
                          <button className="btn btn-sm" style={{ marginLeft: 6 }} onClick={() => setEditing(l)}>Edit roles</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
      </Section>
      <InviteLeaderForm onDone={reload} />
      {editing && <EditRolesModal leader={editing} onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); reload(); }} />}
    </>
  );
}

function EditRolesModal({ leader, onClose, onSaved }) {
  const [eventLeader, setEventLeader] = useState(!!leader.eventLeader);
  const [groupLeader, setGroupLeader] = useState(!!leader.groupLeader);
  const [busy, setBusy] = useState(false);
  const name = [leader.firstname, leader.lastname].filter(Boolean).join(' ') || leader.username || leader.email;
  const removingAll = !eventLeader && !groupLeader;
  const removingEvent = leader.eventLeader && !eventLeader;
  const removingGroup = leader.groupLeader && !groupLeader;

  const save = async () => {
    if (removingAll && !window.confirm(
      `Remove all leader roles from ${name}?\n\nThey become a regular member. Since leaders don't pay, they'll be asked to start a subscription the next time they open the app.`)) return;
    setBusy(true);
    try { await api.setLeaderRoles(leader.id, { eventLeader, groupLeader }); onSaved(); }
    catch (e) { alert(leaderErr(e)); setBusy(false); }
  };

  return (
    <Modal title={`Leader roles — ${name}`} onClose={onClose}
      footer={<>
        <button className="btn" disabled={busy} onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" disabled={busy || (eventLeader === !!leader.eventLeader && groupLeader === !!leader.groupLeader)}
          onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
      </>}>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, cursor: 'pointer' }}>
        <input type="checkbox" checked={eventLeader} onChange={(e) => setEventLeader(e.target.checked)} />
        <span><b>Event Leader</b> — creates and runs events</span>
      </label>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
        <input type="checkbox" checked={groupLeader} onChange={(e) => setGroupLeader(e.target.checked)} />
        <span><b>Group Leader</b> — creates and runs groups</span>
      </label>
      {(removingEvent || removingGroup) && (
        <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, padding: 12, marginTop: 12, fontSize: '0.875rem' }}>
          Nothing they made is deleted: their {removingEvent && removingGroup ? 'events and groups' : removingEvent ? 'events' : 'groups'} keep
          running and they can still manage them — they just can't create new ones.
          {removingAll && <> They become a regular member and will be asked to start a subscription.</>}
        </div>
      )}
    </Modal>
  );
}

function InviteLeaderForm({ onDone }) {
  const empty = { firstname: '', lastname: '', email: '', eventLeader: false, groupLeader: true };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.email.trim()) return;
    if (!f.eventLeader && !f.groupLeader) { alert('Choose Event Leader, Group Leader or both.'); return; }
    setBusy(true); setMsg(null);
    try {
      const r = await api.inviteLeader({ ...f, email: f.email.trim() });
      setMsg(r?.created
        ? `Invited ${f.email.trim()} — they'll get an email with a temporary password.`
        : `${f.email.trim()} already had an account — the role was added and they've been emailed.`);
      setF(empty);
      onDone?.();
    } catch (err) { alert(leaderErr(err)); }
    setBusy(false);
  };

  return (
    <Section title="Invite a leader">
      <form onSubmit={submit} style={{ display: 'grid', gap: 10, maxWidth: 520 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input className="input" placeholder="First name" value={f.firstname} onChange={set('firstname')} />
          <input className="input" placeholder="Last name" value={f.lastname} onChange={set('lastname')} />
        </div>
        <input className="input" type="email" required placeholder="Email *" value={f.email} onChange={set('email')} />
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={f.groupLeader} onChange={set('groupLeader')} /> Group Leader
          </label>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={f.eventLeader} onChange={set('eventLeader')} /> Event Leader
          </label>
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>
          New people get an email with a temporary password and set their own on first sign-in.
          Existing members just get the role. Leaders don't pay a subscription.
        </div>
        <div>
          <button className="btn btn-primary" disabled={busy || !f.email.trim()}>{busy ? 'Inviting…' : 'Invite leader'}</button>
        </div>
        {msg && <div style={{ color: 'var(--green-text, #166534)', fontSize: '0.875rem' }}>{msg}</div>}
      </form>
    </Section>
  );
}
